import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FileIcon from '@mui/icons-material/Task';
import Button from '@mui/material/Button';
import UploadHandler from './UploadHandler';
import useAlert from '../../../toast/useAlert';

// taken from https://stackoverflow.com/a/14919494
function humanFileSize(size: number) {
  let bytes = size;
  if (Math.abs(bytes) < 1000) return `${bytes} B`;

  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let unit = -1;
  do {
    bytes /= 1000;
    unit += 1;
  } while (
    Math.round(Math.abs(bytes) * 10) / 10 >= 1000 &&
    unit < units.length - 1
  );

  return `${bytes.toFixed(1)} ${units[unit]}`;
}

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<any> | any;
}

export default function UploadDialog({
  open,
  onClose,
  onUpload,
}: UploadDialogProps) {
  const { t } = useTranslation();
  const alert = useAlert();

  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = useCallback(async (newFile: File | null) => {
    if (!newFile) {
      return;
    }
    setFile(newFile);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    try {
      await onUpload(file);
    } catch {
      alert.error({ message: t('form.fileField.uploadError') });
    }
  }, [alert, file, onUpload, t]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('form.fileField.uploadDialogTitle')}</DialogTitle>
      <DialogContent>
        {!file ? (
          <UploadHandler onFileChange={handleFileChange} />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row',
              gap: '16px',
              color: 'rgb(0, 119, 187)',
              fontSize: '16px',
              fontWeight: '700',
              height: '200px',
            }}
          >
            <FileIcon fontSize="large" />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div>
                {t('form.fileField.fileName')}: {file.name}
              </div>
              <div>
                {t('form.fileField.fileSize')}: {humanFileSize(file.size)}
              </div>
            </div>
          </div>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginTop: '16px' }}
        >
          <Button
            variant="outlined"
            color="error"
            disabled={!file}
            onClick={() => {
              setFile(null);
            }}
          >
            <DeleteIcon />
          </Button>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={onClose}>
              {t('form.fileField.cancelButton')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={!file}
              onClick={handleUpload}
            >
              {t('form.fileField.uploadButton')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
